import {
  WardrobeZoneType,
  type WardrobeColumnConfiguration,
  type WardrobeZone,
} from '~/types/wardrobeConfigurationTypes'
import { WardrobeTemplateId } from '~/config/wardrobeTemplates'

/** A flat PAL (chipboard) panel: a [width, height] cut rectangle. */
export interface InteriorPanel {
  width: number
  height: number
  quantity: number
}

// Mirror of the 3D builder constants (FURNITURE_CONFIG + zone renderers). These
// drive how interior PAL panels are sized so pricing matches what the 3D scene
// actually draws. Keep in sync with the components referenced in comments.
const PANEL_THICKNESS = 2 // FURNITURE_CONFIG.panelThickness
const PANEL_SPACING = 0.2 // FURNITURE_CONFIG.panelSpacing
const DRAWER_INSIDE_OFFSET = 5 // FURNITURE_CONFIG.drawerInsidePanelsOffset
const SHELF_THICKNESS = 2

// Drawer-zone layout (WardrobeZoneRenderer DRAWERS case).
const DRAWER_MARGIN = 3
const BOTTOM_DRAWER_MARGIN = 5
const DRAWER_SHELF_RESERVE = 3 + SHELF_THICKNESS // SHELF_TOP_MARGIN + SHELF_HEIGHT

// Premium-top-with-drawers fixed layout.
const PREMIUM_DRAWER_COUNT = 3
const PREMIUM_DRAWER_HEIGHT = 19

interface InteriorPanelsArgs {
  /** Per-column outer widths (cm); same array the doors/dividers use. */
  columnWidths: number[]
  /** Total wardrobe width (cm), used to derive equal columns when widths absent. */
  width: number
  columns: number
  depth: number
  height: number
  columnConfigurations?: WardrobeColumnConfiguration[]
}

/** A horizontal shelf panel: cut face is columnWidth × (columnDepth − 4). */
function shelf(columnWidth: number, columnDepth: number): InteriorPanel {
  return { width: columnWidth, height: columnDepth - 4, quantity: 1 }
}

/**
 * The PAL panels of one drawer box: front + bottom + two sides. Mirrors
 * drawerUtils.ts (calculateDrawerDimensions + setup*Panel). Runners/handles are
 * metal and excluded.
 */
function drawerPanels(
  drawerWidth: number,
  drawerHeight: number,
  drawerDepth: number
): InteriorPanel[] {
  const innerHeight = drawerHeight - PANEL_SPACING
  const innerWidth = drawerWidth - PANEL_THICKNESS * 2
  const innerDepth = drawerDepth - PANEL_THICKNESS * 2
  const sideHeight = innerHeight - DRAWER_INSIDE_OFFSET

  return [
    { width: drawerWidth, height: innerHeight, quantity: 1 }, // front
    { width: innerWidth, height: innerDepth, quantity: 1 }, // bottom
    { width: innerDepth, height: sideHeight, quantity: 2 }, // left + right
    { width: innerWidth, height: sideHeight, quantity: 1 }, // back
  ]
}

/** Drawer heights inside a DRAWERS/DRAWERS_WITH_DIVIDERS zone (renderer logic). */
function drawerZoneHeights(zone: WardrobeZone, count: number): number[] {
  if (zone.drawerHeights && zone.drawerHeights.length === count) {
    return zone.drawerHeights
  }
  const totalMargins = BOTTOM_DRAWER_MARGIN + DRAWER_MARGIN * (count - 1)
  const available = zone.height - DRAWER_SHELF_RESERVE - totalMargins
  const even = available / count
  return Array(count).fill(even)
}

function isPremiumTemplate(templateId?: string): boolean {
  return (
    templateId === WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP ||
    templateId === WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS
  )
}

/**
 * Enumerate every interior PAL panel the 3D scene draws for a wardrobe — shelves
 * (zone, top-of-zone, auto above 200cm) and drawer boxes — so they get priced
 * alongside the carcass. Returns flat [width, height] panels with quantity.
 *
 * Sources mirrored: WardrobeColumn.tsx (auto shelves), WardrobeZoneRenderer.tsx
 * (zone shelves + drawers), DoubleHangingPremiumTop(.WithDrawers).tsx.
 */
export function computeInteriorPanels(args: InteriorPanelsArgs): InteriorPanel[] {
  const { columns, depth: D, height: H, columnConfigurations } = args

  // Fall back to equal-width columns when explicit widths aren't supplied,
  // matching the builder's defaultColumnWidth = width / columns.
  const widths =
    args.columnWidths.length === columns
      ? args.columnWidths
      : Array.from({ length: columns }, () => args.width / columns)

  const panels: InteriorPanel[] = []

  for (let i = 0; i < columns; i++) {
    const W = widths[i]
    if (!W) continue

    const config = columnConfigurations?.[i]
    const premium = isPremiumTemplate(config?.templateId)

    // Auto shelves above 200cm — only when NOT a premium-top template.
    if (!premium) {
      if (H >= 237) panels.push(shelf(W - 1, D))
      if (H > 260 && H <= 300) panels.push(shelf(W - 1, D))
      if (H > 300) panels.push(shelf(W - 1, D), shelf(W - 1, D), shelf(W - 1, D))
    }

    // Premium templates render their own interior; skip the zone loop.
    if (config?.templateId === WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP) {
      if (H >= 220) panels.push(shelf(W, D)) // bottom shelf @40cm
      if (H >= 250) panels.push(shelf(W, D)) // top shelf @220cm
      continue
    }
    if (
      config?.templateId ===
      WardrobeTemplateId.DOUBLE_HANGING_PREMIUM_TOP_WITH_3_DRAWERS
    ) {
      panels.push(shelf(W - 1, D)) // shelf above drawers
      for (let d = 0; d < PREMIUM_DRAWER_COUNT; d++) {
        panels.push(...drawerPanels(W - 8, PREMIUM_DRAWER_HEIGHT, D - 5))
      }
      continue
    }

    // Standard zone-by-zone interior.
    for (const zone of config?.zones ?? []) {
      if (zone.addShelfAtTheTop) panels.push(shelf(W - 1, D))

      switch (zone.type) {
        case WardrobeZoneType.SHELVES:
        case WardrobeZoneType.SHELVES_FIXED: {
          const count = zone.shelfCount ?? 0
          for (let s = 0; s < count; s++) panels.push(shelf(W - 1, D))
          break
        }
        case WardrobeZoneType.DRAWERS:
        case WardrobeZoneType.DRAWERS_WITH_DIVIDERS: {
          const count = zone.drawerCount ?? 0
          if (count > 0) {
            const heights = drawerZoneHeights(zone, count)
            for (let d = 0; d < count; d++) {
              panels.push(...drawerPanels(W - 8, heights[d], D - 5))
            }
          }
          break
        }
        default:
          break
      }
    }
  }

  return panels
}
